import { Button, DialogTitle, Text } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FiTrash2 } from "react-icons/fi"

import { TableRowsService } from "@/client"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog"
import useCustomToast from "@/hooks/useCustomToast"

interface DeleteTableRowProps {
  tableId: string
  tableRowId: string
}

const DeleteTableRow = ({ tableId, tableRowId }: DeleteTableRowProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm()

  const deleteRow = async () => {
    await TableRowsService.deleteTableRow({ tableId, tableRowId })
  }

  const mutation = useMutation({
    mutationFn: deleteRow,
    onSuccess: () => {
      showSuccessToast("Row deleted successfully")
      setIsOpen(false)
    },
    onError: () => {
      showErrorToast("Failed to delete the row")
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["openSearchDocs", { tableId }] })
    },
  })

  const onSubmit = () => {
    mutation.mutate()
  }

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      role="alertdialog"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" colorPalette="red">
          <FiTrash2 fontSize="16px" />
          데이터 삭제
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>데이터 삭제</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>
              이 데이터는 영구적으로 삭제됩니다. 진행하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </Text>
          </DialogBody>

          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button
                variant="subtle"
                colorPalette="gray"
                disabled={isSubmitting}
              >
                취소
              </Button>
            </DialogActionTrigger>
            <Button
              variant="solid"
              colorPalette="red"
              type="submit"
              loading={isSubmitting}
            >
              삭제
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  )
}

export default DeleteTableRow
